import { Injectable } from "@angular/core";
import { HttpQueryManager, type Params, type QueryParamProvider } from "@shared/http-query.manager";
import type { IngestModalUrlState, IngestModalUrlStep } from "./ingest-modal.service";

/** URL parameter that mirrors the ingest modal, e.g. /ingest/library;i=f.203.45 */
export const INGEST_MODAL_PARAM = "i";

const STEP_CODES: Record<IngestModalUrlStep, string> = { refine: "r", format: "f", action: "a" };

// `<step>.<corpusId>[.<pifId>]`, e.g. "r.203", "f.203.45"
export const ingestModalQueryParamProvider: QueryParamProvider<IngestModalUrlState> = {
  name: INGEST_MODAL_PARAM,
  paramToValue: (param: string): Partial<IngestModalUrlState> => {
    const [code, corpusId, pifId] = param.split(".");
    const step = (Object.keys(STEP_CODES) as IngestModalUrlStep[]).find((s) => STEP_CODES[s] === code);
    if (!step) {
      return {};
    }
    return { step, corpusId: corpusId ? Number(corpusId) : undefined, pifId: pifId ? Number(pifId) : undefined };
  },
  valueToParam: (state: Partial<IngestModalUrlState>) => {
    if (!state.step) {
      return null;
    }
    return [STEP_CODES[state.step], state.corpusId, state.pifId].filter((v) => v !== undefined).join(".");
  },
};

/** Translate the ingest modal's state to/from the URL */
@Injectable({ providedIn: "root" })
export class IngestWindowLocationService extends HttpQueryManager<IngestModalUrlState> {
  // true while the current history entry was pushed by the modal (false after a reload or on a shared link)
  #pushedEntry = false;

  constructor() {
    super();
    this.registerProvider(ingestModalQueryParamProvider);
  }

  stateToUrlSuffix(state: IngestModalUrlState): string {
    const params = this.stateToParams(state);
    return Object.keys(params)
      .map((name) => `${name}=${encodeURIComponent(params[name])}`)
      .join(";");
  }

  /** The matrix params on the last path segment of the browser's current URL. */
  getUrlParams(): Params {
    const lastSegment = window.location.pathname.split("/").pop() ?? "";
    return Object.fromEntries(
      lastSegment
        .split(";")
        .slice(1)
        .map((pair) => {
          const [name, ...value] = pair.split("=");
          return [name, decodeURIComponent(value.join("="))];
        }),
    );
  }

  /** Reflects the modal in the URL: replaces the current entry if it already shows the modal, otherwise pushes one. */
  updateBrowserHistory(state: IngestModalUrlState | null) {
    const { origin, pathname, search, hash } = window.location;

    // matrix params belong to a segment: change only ours on the last one, leave everything else
    const segments = pathname.split("/");
    const [name, ...params] = (segments.pop() ?? "").split(";");
    const others = params.filter((param) => param.split("=")[0] !== INGEST_MODAL_PARAM);
    const own = state ? [this.stateToUrlSuffix(state)] : [];
    segments.push([name, ...others, ...own].join(";"));

    const href = origin + segments.join("/") + search + hash;
    if (href === window.location.href) {
      return;
    }
    const alreadyShowsModal = INGEST_MODAL_PARAM in this.getUrlParams();
    if (alreadyShowsModal) {
      history.replaceState(history.state, "Pienso Platform", href);
    } else {
      history.pushState(null, "Pienso Platform", href);
      this.#pushedEntry = true;
    }
  }

  /** Removes the modal from the URL: undoes the entry the modal pushed, or rewrites the current one (e.g. a shared link). */
  clearBrowserHistory() {
    if (this.#pushedEntry) {
      this.#pushedEntry = false;
      history.back();
    } else {
      this.updateBrowserHistory(null);
    }
  }

  /** Call when the browser navigated away from the modal's entry on its own (Back button). */
  forgetPushedEntry() {
    this.#pushedEntry = false;
  }
}
